import { cn } from '@/lib/cn';

/**
 * The coin symbols, drawn inline.
 *
 * Wikimedia Commons was the obvious source and turned out to carry only two
 * usable marks: Bitcoin and Ethereum. What it has for Binance, Solana, XRP and
 * Tether are horizontal COMPANY WORDMARKS — "BINANCE", "XRP LEDGER" — which
 * dissolve into a smudge inside a 32px circle. Dogecoin and Avalanche are not
 * there at all under a licence we could use.
 *
 * So they are all drawn here instead: one source, one visual weight, no extra
 * requests, and nothing whose licence has to be traced later. Each is the
 * geometry of the mark rather than a trace of a specific file — a ₿ with two
 * bars, Ethereum's octahedron, Solana's three slanted bars, and so on. They are
 * used to identify the instrument in a price list, which is what a ticker
 * symbol is for.
 */
export type CoinCode = 'BTC' | 'ETH' | 'SOL' | 'XRP' | 'DOGE' | 'BNB' | 'AVAX' | 'PAXG' | 'USDT';

interface Mark {
  /** The disc behind the glyph. */
  background: string;
  /** The glyph itself, on that disc. */
  foreground: string;
  path: string;
}

const marks: Record<CoinCode, Mark> = {
  // ₿ — the B with two vertical strokes through it.
  BTC: {
    background: '#f7931a',
    foreground: '#ffffff',
    path: 'M15.1 10.5c.2-1.5-.9-2.3-2.5-2.8l.5-2.1-1.3-.3-.5 2c-.3-.1-.7-.2-1-.2l.5-2.1-1.3-.3-.5 2.1-.8-.2-1.8-.4-.3 1.4s1 .2 1 .2c.5.1.6.5.6.7l-.6 2.4h.1l-.1.1-.8 3.3c-.1.2-.2.4-.6.3 0 0-1-.2-1-.2L4 16.1l1.7.4.9.2-.5 2.2 1.3.3.5-2.1c.4.1.7.2 1 .3l-.5 2.1 1.3.3.5-2.2c2.2.4 3.9.3 4.6-1.8.6-1.7 0-2.6-1.2-3.2.9-.2 1.5-.8 1.6-2zm-2.9 4.3c-.4 1.7-3.2.8-4.1.6l.7-2.9c.9.2 3.9.7 3.4 2.3zm.4-4.3c-.4 1.5-2.7.8-3.5.6l.6-2.6c.8.2 3.2.6 2.9 2z',
  },
  // The octahedron: two triangles above, two below.
  ETH: {
    background: '#627eea',
    foreground: '#ffffff',
    path: 'M12 3v6.6l5.6 2.5L12 3zm0 0L6.4 12.1 12 9.6V3zm0 13.1v4.9l5.6-7.8-5.6 2.9zm0 4.9v-4.9l-5.6-2.9L12 21zm0-5.9l5.6-2.9-5.6-2.5v5.4zm-5.6-2.9l5.6 2.9v-5.4l-5.6 2.5z',
  },
  // Three slanted bars, the middle one running the other way.
  SOL: {
    background: '#0b0b12',
    foreground: '#14f195',
    path: 'M6.2 15.4c.1-.1.3-.2.5-.2h11c.3 0 .4.3.2.5l-2.1 2.1c-.1.1-.3.2-.5.2h-11c-.3 0-.4-.3-.2-.5l2.1-2.1zm0-8.1c.1-.1.3-.2.5-.2h11c.3 0 .4.3.2.5l-2.1 2.1c-.1.1-.3.2-.5.2h-11c-.3 0-.4-.3-.2-.5l2.1-2.1zm9.6 4c-.1-.1-.3-.2-.5-.2h-11c-.3 0-.4.3-.2.5l2.1 2.1c.1.1.3.2.5.2h11c.3 0 .4-.3.2-.5l-2.1-2.1z',
  },
  // The ✕ formed by two crossing chevron pairs.
  XRP: {
    background: '#23292f',
    foreground: '#ffffff',
    path: 'M16.9 5h2.4l-4.9 4.9c-1.3 1.3-3.5 1.3-4.8 0L4.7 5h2.4l3.7 3.7c.7.7 1.8.7 2.4 0L16.9 5zM7.1 19H4.7l5-4.9c1.3-1.3 3.5-1.3 4.8 0l4.9 4.9h-2.4l-3.7-3.7c-.7-.7-1.8-.7-2.4 0L7.1 19z',
  },
  // Ð — the crossed D.
  DOGE: {
    background: '#c2a633',
    foreground: '#ffffff',
    path: 'M9 5h3.6c3.5 0 5.6 2.6 5.6 7s-2.1 7-5.6 7H9v-5.6H7.2v-2H9V5zm2.5 2.2v3.2h2.1v2h-2.1v4.4h1c2 0 3.2-1.6 3.2-4.8s-1.2-4.8-3.2-4.8h-1z',
  },
  // A square on its point, with four smaller ones around it.
  BNB: {
    background: '#f3ba2f',
    foreground: '#ffffff',
    path: 'M12 3l2.5 2.5L9.6 10.4 7.1 7.9 12 3zm4.1 4.1L18.6 9.6 9.6 18.6 7.1 16.1l9-9zM4.5 10.5L7 13l-2.5 2.5L2 13l2.5-2.5zm15 0L22 13l-2.5 2.5L17 13l2.5-2.5zM12 15.6l2.5 2.5L12 20.6 9.5 18.1l2.5-2.5z',
  },
  // The A of a mountain, cut by a slope.
  AVAX: {
    background: '#e84142',
    foreground: '#ffffff',
    path: 'M13.6 8.6c.5-.9 1.4-.9 1.9 0l3.4 6c.5.9.1 1.6-1 1.6h-6.8c-1 0-1.5-.7-1-1.6l3.5-6zm-6 4c.5-.9 1.3-.9 1.8 0l.9 1.5c.4.7.4 1.3 0 2l-.9 1.5c-.5.9-1.3.9-1.8 0l-.9-1.5c-.4-.7-.4-1.3 0-2l.9-1.5z',
  },
  // A bar, for the gold-backed token.
  PAXG: {
    background: '#d4af37',
    foreground: '#ffffff',
    path: 'M4 15h16l-1.5 3.5H5.5L4 15zm2-4.5h12L16.8 14H7.2L6 10.5zM8 6h8l-1.2 3.5H9.2L8 6z',
  },
  // The T on a disc.
  USDT: {
    background: '#26a17b',
    foreground: '#ffffff',
    path: 'M13.4 11.6v-1.8h4.1V7H6.5v2.8h4.1v1.8c-3.3.2-5.8.8-5.8 1.6s2.5 1.5 5.8 1.6v5.1h2.8v-5.1c3.3-.2 5.8-.8 5.8-1.6s-2.5-1.5-5.8-1.6zm0 2.8c-.1 0-.6.1-1.4.1-.7 0-1.2 0-1.4-.1-2.8-.1-4.9-.6-4.9-1.2s2.1-1.1 4.9-1.2v2c.2 0 .7.1 1.4.1.8 0 1.3 0 1.4-.1v-2c2.8.1 4.9.6 4.9 1.2s-2.1 1.1-4.9 1.2z',
  },
};

export function CoinMark({ code, className }: { code: CoinCode; className?: string }) {
  const mark = marks[code];

  return (
    <span
      aria-hidden
      className={cn('grid shrink-0 place-items-center rounded-full', className)}
      style={{ background: mark.background }}
    >
      <svg viewBox="0 0 24 24" className="size-[62%]" style={{ fill: mark.foreground }}>
        <path d={mark.path} />
      </svg>
    </span>
  );
}
